/** How long a listener stays open after its last holder lets go, so a quick return shows the retained data at once. */
export const RETAIN_LINGER_MS = 45_000

/** What a retainer opens and closes; `close` must also reset the owning store state to loading. */
export interface RetainerHandlers {
  open: () => void
  close: () => void
}

/** A reference-counted owner of one live listener (or one group of listeners). */
export interface Retainer {
  /** Registers a holder, opening the listener for the first one; the returned function releases that holder once. */
  retain: () => () => void
  /** Closes the listener and resets its state now, ignoring holders and linger; holders stay counted and wait for `resume`. */
  teardown: () => void
  /** Re-opens a torn-down listener that still has holders. */
  resume: () => void
  /** Closes the listener for good and removes the retainer from the registry. */
  dispose: () => void
  /** How many holders are alive. */
  readonly holders: number
  /** Whether the listener is open. */
  readonly isOpen: boolean
}

const registry = new Set<Retainer>()
const teardownHooks = new Set<() => void>()

/** Creates a retainer and registers it so `releaseAllRetained` can reach it. */
export function createRetainer(handlers: RetainerHandlers, lingerMs = RETAIN_LINGER_MS): Retainer {
  let holders = 0
  let isOpen = false
  let timer: ReturnType<typeof setTimeout> | null = null

  function cancelLinger(): void {
    if (timer === null) return
    clearTimeout(timer)
    timer = null
  }

  function openNow(): void {
    if (isOpen) return
    isOpen = true
    handlers.open()
  }

  function closeNow(): void {
    cancelLinger()
    if (!isOpen) return
    isOpen = false
    handlers.close()
  }

  const retainer: Retainer = {
    retain() {
      holders += 1
      cancelLinger()
      openNow()
      let released = false
      return () => {
        if (released) return
        released = true
        holders -= 1
        if (holders === 0 && isOpen) timer = setTimeout(closeNow, lingerMs)
      }
    },
    teardown: closeNow,
    resume() {
      if (holders > 0) openNow()
    },
    dispose() {
      closeNow()
      holders = 0
      registry.delete(retainer)
    },
    get holders() {
      return holders
    },
    get isOpen() {
      return isOpen
    },
  }
  registry.add(retainer)
  return retainer
}

/** Registers a reset for keyed, short-lived feeds that a scope change must also clear; returns its remover. */
export function onRetainedTeardown(hook: () => void): () => void {
  teardownHooks.add(hook)
  return () => {
    teardownHooks.delete(hook)
  }
}

/** Closes every retained listener and resets its state at once; call on sign-out and on any workspace or brand switch. */
export function releaseAllRetained(): void {
  for (const retainer of registry) retainer.teardown()
  for (const hook of teardownHooks) hook()
}

/** Re-opens every torn-down listener that still has a holder, under the scope now in force. */
export function resumeRetained(): void {
  for (const retainer of registry) retainer.resume()
}

/** Drops every registered retainer without closing it; for test isolation only. */
export function clearRetainerRegistry(): void {
  registry.clear()
  teardownHooks.clear()
}
